import type {
  CorrespondenceThread,
  CorrespondenceThreadParticipant,
} from "@platform/api";

/**
 * What one row of the thread list shows.
 *
 * Derived here rather than in the row so the fallbacks are testable without
 * rendering, and so the places where a listing carries less than the design
 * asks for are stated once.
 */
export interface ThreadSummary {
  id: string;
  /**
   * Who the conversation is with: the first participant from outside Centro,
   * by display name where the directory gave one and by address otherwise.
   *
   * Not the most recent sender, which is what the design puts on the row. A
   * listing carries participants but no messages, so who spoke last is not
   * available to derive. The two agree on a thread with one counterparty and
   * diverge once a second outside address joins.
   */
  counterparty: string;
  /**
   * The carrier the thread is with, shown beside the name as `· Guardian`.
   *
   * Absent on a thread that is not with a carrier, and on one whose addresses
   * the RFP's directory does not hold. The row drops the suffix rather than
   * showing an empty separator.
   */
  carrierName?: string;
  /**
   * What the conversation is about, as the row's chips.
   *
   * Derived by the BFF from the RFP's coverages, so an unlinked thread and a
   * cross-RFP listing both carry none — there is no RFP to derive from in one
   * case and it is not paid for in the other.
   */
  tags: string[];
  /**
   * Every address on the thread, lowercased, for search.
   *
   * Kept apart from `counterparty` because that one is a label: once the
   * directory supplies a display name the address stops appearing in it, and
   * searching the label alone would mean an analyst can find a carrier by
   * address only while nobody has named them.
   */
  addresses: string[];
  initials: string;
  /**
   * Inbound messages the signed-in analyst has not read.
   *
   * Zero renders no badge rather than a `0 unread`: the row says what is
   * waiting, and nothing waiting is said by the badge's absence.
   */
  unreadCount: number;
  /**
   * RUMI has written a reply nobody has sent.
   *
   * Badged on the row because it is work waiting for the analyst in a
   * different sense from unread mail: something is ready for them to approve
   * rather than ready for them to read.
   */
  hasRumiDraft: boolean;
  /**
   * An unsent reply from anyone, which is what the drafts listing counts.
   *
   * Kept apart from `hasRumiDraft` because an analyst's own half-written
   * reply belongs in that listing and is not a thing to label as RUMI's.
   */
  hasDraft: boolean;
  subject: string;
  /** `lastMessageAt`, as a short day the row can sit a status chip beside. */
  activity: string;
  status: ThreadStatusTreatment;
  /** Absent while the thread is unlinked, which is the triage state. */
  rfpCanonicalIdentifier?: string;
}

export interface ThreadStatusTreatment {
  label: string;
  /** A `Chip` status, which the design system names `color`. */
  color: "attention" | "info" | "default";
}

/**
 * The reply-state axis, which is what a thread actually carries.
 *
 * The design's row-end chip shows a different axis — Figma annotates those
 * three as "RUMI Processing States" — and no read path exposes one: the
 * proto keeps quarantine off listings deliberately ("No read path returns a
 * quarantined message"). Rendering reply state here puts a true word in the
 * slot the design reserved, and the processing chip arrives with the field
 * that backs it.
 */
const STATUS_TREATMENTS: Record<string, ThreadStatusTreatment> = {
  THREAD_STATUS_NEEDS_REPLY: { label: "Needs reply", color: "attention" },
  THREAD_STATUS_AWAITING_REPLY: { label: "Awaiting reply", color: "info" },
  THREAD_STATUS_CLOSED: { label: "Closed", color: "default" },
};

const UNKNOWN_STATUS: ThreadStatusTreatment = {
  label: "Unknown",
  color: "default",
};

/**
 * Two letters for the avatar.
 *
 * Takes the first letter of each of the first two words for a display name,
 * and of the address's local part otherwise — `dana.ruiz@guardian.com` gives
 * `DR` the same way `Dana Ruiz` does, so a thread whose counterparty is only
 * ever an address still gets a stable, meaningful pair rather than `DA`.
 */
export const initialsFor = (nameOrAddress: string): string => {
  const [localPart] = nameOrAddress.trim().split("@");
  const words = localPart.split(/[\s._-]+/u).filter(Boolean);

  if (words.length === 0) return "";

  const letters =
    words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[1][0]}`;

  return letters.toUpperCase();
};

/**
 * The short day beside the subject, in the viewer's locale.
 *
 * No year: the rows are ordered by activity and a correspondence tab is read
 * for what moved recently, so the year is noise on all but the oldest row. An
 * unparseable timestamp renders empty rather than `Invalid Date`.
 */
export const formatActivity = (
  timestamp: string | undefined,
  locale?: string,
): string => {
  if (!timestamp) return "";

  const at = new Date(timestamp);

  if (Number.isNaN(at.getTime())) return "";

  return at.toLocaleDateString(locale, { month: "short", day: "numeric" });
};

export const statusTreatment = (
  status: string | undefined,
): ThreadStatusTreatment =>
  (status && STATUS_TREATMENTS[status]) || UNKNOWN_STATUS;

/**
 * Who the thread is with.
 *
 * The first participant whose party is not `internal`, which is the carrier
 * or broker the conversation is with; participants arrive oldest first, so
 * that is the address the thread opened against. An internal-only thread has
 * no outside party, so the first participant stands instead, and a thread
 * carrying none at all falls back to whoever opened it.
 *
 * `displayName` where the directory matched the address and the address
 * otherwise — `unknown` is a real party, and an address nobody recognises
 * still has to appear on the row.
 */
export const counterpartyOf = (thread: CorrespondenceThread): string => {
  const participants: CorrespondenceThreadParticipant[] =
    thread.participants ?? [];

  const outside = participants.find(
    (participant) => participant.party !== "internal",
  );

  const named = outside ?? participants[0];

  return named?.displayName || named?.address || thread.createdBy || "";
};

export const toThreadSummary = (
  thread: CorrespondenceThread,
  locale?: string,
): ThreadSummary => {
  const counterparty = counterpartyOf(thread);

  return {
    id: thread.id ?? "",
    counterparty,
    addresses: (thread.participants ?? [])
      .map((participant) => participant.address?.toLowerCase())
      .filter((address): address is string => !!address),
    initials: initialsFor(counterparty),
    unreadCount: thread.unreadCount ?? 0,
    hasRumiDraft: thread.hasRumiDraft ?? false,
    hasDraft: thread.hasDraft ?? false,
    subject: thread.subject ?? "",
    carrierName: thread.carrierName || undefined,
    tags: thread.tags ?? [],
    activity: formatActivity(thread.lastMessageAt, locale),
    status: statusTreatment(thread.status),
    rfpCanonicalIdentifier: thread.rfpCanonicalIdentifier ?? undefined,
  };
};
